import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Business information state for borrower/merchant onboarding
 * Stores company details, registration, and contact information
 */
export interface BizState {
  // Company identification
  company: string | null;
  regNumber: string | null;

  // Legal structure
  legalStructure: string | null;
  legalStructureOthers: string | null;

  // Industry classification
  industryGroup: string | null;
  industryDropdownValues: any | null; // TODO: Define proper type
  industryOthers: string | null;
  typeOfInstitute: string | null;

  // Location details
  countryOfIncorp: string | null;
  countryOfDomicile: string | null;
  address: string | null;
  postal: string | null;

  // Contact information
  website: string | null;
  phoneNumber: string | null;

  // Dates
  incorporationDate: string | null; // ISO date string
}

const initialState: BizState = {
  company: null,
  regNumber: null,
  legalStructure: null,
  legalStructureOthers: null,
  industryGroup: null,
  industryDropdownValues: null,
  typeOfInstitute: null,
  industryOthers: null,
  countryOfIncorp: null,
  countryOfDomicile: null,
  address: null,
  postal: null,
  website: null,
  phoneNumber: null,
  incorporationDate: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof BizState>(field: K) => {
  return (state: BizState, action: PayloadAction<BizState[K]>) => {
    state[field] = action.payload;
  };
};

const bizBorSlice = createSlice({
  name: "borBiz",
  initialState,
  reducers: {
    // Company identification
    setCompanyRedux: createFieldSetter("company"),
    setRegNumberRedux: createFieldSetter("regNumber"),
    setIncorporationDateRedux: createFieldSetter("incorporationDate"),

    // Legal structure
    setLegalStructureRedux: createFieldSetter("legalStructure"),
    setLegalStructureOthersRedux: createFieldSetter("legalStructureOthers"),
    setTypeOfInstituteRedux: createFieldSetter("typeOfInstitute"),

    // Industry classification
    setIndustryGroupRedux: createFieldSetter("industryGroup"),
    setIndustryDropdownValuesRedux: createFieldSetter("industryDropdownValues"),
    setIndustryOthersRedux: createFieldSetter("industryOthers"),

    // Location and contact
    setCountryOfIncorpRedux: createFieldSetter("countryOfIncorp"),
    setCountryOfDomicileRedux: createFieldSetter("countryOfDomicile"),
    setAddressRedux: createFieldSetter("address"),
    setPostalRedux: createFieldSetter("postal"),
    setWebsiteRedux: createFieldSetter("website"),
    setPhoneNumber: createFieldSetter("phoneNumber"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateBizState: (state, action: PayloadAction<Partial<BizState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetBizState: () => initialState,
  },
});

export const {
  setCompanyRedux,
  setRegNumberRedux,
  setLegalStructureRedux,
  setLegalStructureOthersRedux,
  setTypeOfInstituteRedux,
  setIndustryGroupRedux,
  setIndustryDropdownValuesRedux,
  setIndustryOthersRedux,
  setCountryOfIncorpRedux,
  setCountryOfDomicileRedux,
  setPostalRedux,
  setWebsiteRedux,
  setAddressRedux,
  setPhoneNumber,
  setIncorporationDateRedux,
  hydrateBizState,
  resetBizState,
} = bizBorSlice.actions;

export default bizBorSlice.reducer;
